import { useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AuthContext } from "../context/auth-provider";
import { getRedirectURI } from "../components/google-login-button";
import { AuthError } from "../types/form-login-types";
import { statusResponse } from "modules/main/types/status";
import { FetchDataOptions } from "modules/main/services/fetchData";
import { useRecaptchaReady } from "modules/main/hooks/useRecaptchaReady";
import {
  endPointConfirmAccount,
  endPointPasswordReset,
  endPointValidateToken,
  endPointVerifyAccountToken,
} from "modules/main/services/endpoints";
import { LoginForm, RECAPTCHA_SITE_KEY } from "../components/loginDialog";
import { useFetch } from "modules/main/hooks/useFetch";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { OctagonAlert, RefreshCw } from "lucide-react";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { MainLogo } from "modules/main/assets/main_logo";
import { getVisitorId } from "utils/analytics";

const DEFAULT_GOOGLE_LOGIN_REDIRECT = "/store";

export const LoginPage = () => {
  const [params] = useSearchParams();
  const googleCode = params.get("code");
  const token = params.get("token");
  const new_account = params.get("new_account");
  const verify_token = params.get("verify_token");
  const redirectState = params.get("state");
  const hasSpecialAuthFlow = Boolean(
    googleCode || token || new_account || verify_token,
  );
  const navigate = useNavigate();
  const [loginError, setLoginError] = useState<boolean>(false);
  const [isProcessingAuth, setIsProcessingAuth] = useState<boolean>(false);
  const { loginAuth, loginAuthGoogle } = useContext(AuthContext);

  useEffect(() => {
    const runGoogleAuth = async () => {
      setIsProcessingAuth(true);
      // Limpiar inmediatamente la URL para prevenir re-ejecuciones
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete("code");
      newUrl.searchParams.delete("state");
      window.history.replaceState({}, document.title, newUrl.toString());

      try {
        await loginAuthGoogle({
          credential: googleCode ?? "",
          redirect_uri: getRedirectURI(),
        });

        navigate(DEFAULT_GOOGLE_LOGIN_REDIRECT, {
          replace: true,
        });
      } catch (error) {
        console.error("Google authentication failed:", error as AuthError);
        toast.error("No se pudo iniciar sesión con Google. Por favor intenta nuevamente.");
        setLoginError(true);
        setIsProcessingAuth(false); // Resetear en caso de error
      }
    };

    // Prevenir múltiples ejecuciones de autenticación
    if (googleCode && !isProcessingAuth) {
      void runGoogleAuth();
    }
  }, [googleCode]); // Remover dependencias problemáticas

  return (
    <main className="p-8 pt-16 flex justify-center">
      {token && <ResetPasswordDialog token={token} />}
      {new_account && <ValidatednewAccount token={new_account} />}
      {verify_token && <VerifyAccountDeepLink token={verify_token} />}
      {!hasSpecialAuthFlow && <LoginForm loginAuth={loginAuth} />}
    </main>
  );
};

//
//     const [params] = useSearchParams();
//
//
//
//     const googleCode = params.get("code");

//     return (
//         <>
//         {(loginError)&&
//             <AlertDialog open={loginError} onOpenChange={setLoginError}>
//                 <AlertDialogContent>
//                     <AlertDialogHeader>
//                         <AlertDialogTitle>Ha Ocurrido un Error</AlertDialogTitle>
//                         <AlertDialogDescription>Ha ocurrido un error con la autentficación, por favor intenta más tarde</AlertDialogDescription>
//                     </AlertDialogHeader>
//                     <AlertDialogFooter>
//                         <AlertDialogAction>Aceptar</AlertDialogAction>
//                     </AlertDialogFooter>
//                 </AlertDialogContent>
//             </AlertDialog>
//         }
//             <NoLoginHeader loginAuth={loginAuth}/>
//             <main className="flex flex-col items-center py-8 noto-sans-regular gap-16">
//                 <div className="flex flex-col justify-center items-center w-full lg:w-[40%] mx-auto gap-4 px-4 lg:px-2">
//                     <p className="text-center text-2xl lg:text-4xl font-semibold">Ahorra <span className="text-primary font-bold">tiempo</span> y <span className="text-primary font-bold">dinero</span> en tu compra del súper.</p>

//                     <p className="text-sm lg:text-base text-center">Arma tu carrito y deja que Carri compare precios por ti, generando la mejor compra distribuida y ayudándote a ahorrar hasta un 40%.</p>
//                 </div>
//                 <div className="banner-main w-full flex flex-col px-4 gap-8 min-h-80 py-12 text-white text-sm lg:text-base">
//                     <div className="flex w-full gap-8 justify-evenly flex-wrap">
//                         <div className="flex flex-col gap-2 montserrat w-full lg:w-[30%]">
//                             <p className="font-semibold text-xl">¿Cómo funciona?</p>
//                             <p>Carri es un optimizador de compras de supermercado. Arma tu carrito, verifica que no te falte nada y, al finalizar, Carri comparará precios y optimizará tu compra, distribuyendo los productos entre distintos supermercados para aprovechar las mejores ofertas y promociones, además de minimizar los costos de envío. Así, obtendrás el total más económico en comparación con comprar todo en un solo lugar. Si estás de acuerdo con la propuesta, podrás completar tu compra fácilmente en cada supermercado.
//                             </p>
//                         </div>
//                         <div className="flex flex-col montserrat bg-[url('/public/how-works-bg.webp')] bg-cover bg-center w-full lg:w-[30%] h-[230px] justify-start items-center gap-2">
//                             <p className="font-semibold text-base md:text-xl text-center">¿Cómo comenzar a ahorrar con Carri?</p>
//                             <HowWorksDialog embedId="" />
//                         </div>
//                     </div>
//                 </div>

//             </main>
//         </>
//     )
// }

/**
 * Handles account activation via a one-time deep-link token stored in Redis.
 * Triggered by the URL param `?verify_token=...` on the /login page.
 */
const VerifyAccountDeepLink: React.FC<{ token: string }> = ({ token }) => {
  const [triggerFetch, setTriggerFetch] = useState(false);
  const [requestOptions, setRequestOptions] = useState<FetchDataOptions | null>(
    null,
  );
  const [countdown, setCountdown] = useState<number>(3);
  const [authCompleted, setAuthCompleted] = useState(false);
  const { checkAuth } = useContext(AuthContext);
  const navigate = useNavigate();

  type VerifyAccountResponse = {
    status: boolean;
    message?: string;
    user_data?: string;
  };

  const { data, loading } = useFetch<VerifyAccountResponse>(
    triggerFetch ? endPointVerifyAccountToken : null,
    requestOptions ?? undefined,
  );

  // Trigger the request immediately on mount — no reCAPTCHA needed
  useEffect(() => {
    const opts: FetchDataOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: { token },
    };
    setRequestOptions(opts);
    setTriggerFetch(true);
  }, [token]);

  // Run checkAuth when response arrives
  useEffect(() => {
    const run = async () => {
      if (data && data.status) {
        try {
          await checkAuth(data.user_data);
        } catch (err) {
          // checkAuth failure should not block redirect
        } finally {
          setAuthCompleted(true);
        }
      }
    };
    run();
  }, [data, checkAuth]);

  // Countdown only after checkAuth completes
  useEffect(() => {
    if (data?.status && !loading && authCompleted) {
      const timer = setInterval(() => setCountdown((prev) => prev - 1), 1000);
      return () => clearInterval(timer);
    }
  }, [data?.status, loading, authCompleted]);

  // Redirect when countdown reaches 0
  useEffect(() => {
    if (countdown === 0 && data?.status && !loading && authCompleted) {
      navigate("/?new_user=true");
    }
  }, [countdown, data?.status, loading, authCompleted, navigate]);

  return (
    <Card className="w-fit">
      <CardHeader>
        <CardTitle className="text-center">¡Bienvenido!</CardTitle>
        <CardDescription></CardDescription>
      </CardHeader>
      <CardContent>
        <div className="mx-auto grid w-full lg:w-[350px] gap-6">
          <div className="grid gap-5 text-center">
            {loading || !triggerFetch ? (
              <div className="flex flex-row items-center justify-center gap-4 text-muted-foreground text-sm">
                <span>
                  <RefreshCw id="refresh" className="h-8 w-8 text-primary" />
                </span>
                Estamos validando tu cuenta, por favor espera un momento.
              </div>
            ) : data?.status ? (
              <div className="flex flex-col items-center gap-4 text-sm">
                <MainLogo color="#F97316" classname="w-12" />
                Tu cuenta ha sido confirmada con éxito, ¡Bienvenido a Carriapp!
              </div>
            ) : (
              <Alert variant="destructive" className="w-full">
                <OctagonAlert className="h-4 w-4" />
                <AlertTitle>Error al validar tu cuenta</AlertTitle>
                <AlertDescription>
                  El enlace no es válido o ha expirado. Por favor contacta a
                  soporte.
                </AlertDescription>
              </Alert>
            )}
          </div>
          {(!data?.status || loading) && (
            <div className="flex justify-end gap-3">
              <Button type="button" onClick={() => navigate("/")}>
                Volver al Inicio
              </Button>
            </div>
          )}
        </div>
      </CardContent>
      {data?.status && !loading && authCompleted && (
        <CardFooter className="text-center text-sm text-muted-foreground">
          Te redireccionaremos a la página de inicio en {countdown} segundos.
        </CardFooter>
      )}
    </Card>
  );
};

const ValidatednewAccount: React.FC<{ token: string }> = ({
  token,
  ...props
}) => {
  const [open, setOpen] = useState(true);
  const [triggerFetch, setTriggerFetch] = useState(false);
  const [requestOptions, setRequestOptions] = useState<FetchDataOptions | null>(
    null,
  );
  const [countdown, setCountdown] = useState<number>(3);
  const [authCompleted, setAuthCompleted] = useState(false); // Nuevo estado para rastrear si checkAuth se completó
  const reCaptchaIsReady = useRecaptchaReady();
  const { checkAuth } = useContext(AuthContext);
  const navigate = useNavigate();
  type createAccountResponse = {
    status: boolean;
    message?: string;
    user_data?: string;
  };

  const { data, loading, error } = useFetch<createAccountResponse>(
    triggerFetch ? endPointConfirmAccount : null,
    requestOptions ?? undefined,
  );

  useEffect(() => {
    const getRecaptchaToken = async () => {
      if (reCaptchaIsReady) {
        try {
          const recaptchaToken = await window.grecaptcha.execute(
            RECAPTCHA_SITE_KEY,
            { action: "login" },
          );
          const opts: FetchDataOptions = {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "x-visitor-id": getVisitorId(),
            },
            credentials: "include",
            body: {
              token: token,
              recaptchaToken: recaptchaToken,
            },
          };
          setRequestOptions(opts);
          setTriggerFetch(true);
        } catch (error) {
          console.error("Error ejecutando reCAPTCHA:", error);
        }
      }
    };
    getRecaptchaToken();
  }, [reCaptchaIsReady, token]);

  // Efecto para ejecutar checkAuth cuando tengamos los datos
  useEffect(() => {
    const run = async () => {
      if (data && data.status) {
        try {
          await checkAuth(data.user_data);
          setAuthCompleted(true); // Marcar que checkAuth se completó
        } catch (error) {
          console.error("Error in checkAuth:", error);
          setAuthCompleted(true); // Marcar como completado incluso si hay error para no bloquear
        }
      } else {
        console.log("Skipping checkAuth - data or data.status is false/null");
      }
    };

    run();
  }, [data, checkAuth]);

  // Efecto para manejar el countdown (solo después de que checkAuth se complete)
  useEffect(() => {
    if (data?.status && !loading && authCompleted) {
      const timer = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);

      // Cleanup del timer
      return () => clearInterval(timer);
    }
  }, [data?.status, loading, authCompleted]);

  // Efecto separado para manejar la redirección
  useEffect(() => {
    if (countdown === 0 && data?.status && !loading && authCompleted) {
      navigate("/?new_user=true");
    }
  }, [countdown, data?.status, loading, authCompleted, navigate]);

  return (
    <Card className="w-fit">
      <CardHeader>
        <CardTitle className="text-center">¡Bienvenido!</CardTitle>
        <CardDescription></CardDescription>
      </CardHeader>
      <CardContent>
        <div className="mx-auto grid w-full lg:w-[350px] gap-6">
          <div className="grid gap-5 text-center">
            <h1 className="text-2xl font-bold"></h1>
            {loading || !triggerFetch ? (
              <div className="flex flex-row items-center justify-center gap-4  text-muted-foreground text-sm">
                <span className="">
                  <RefreshCw id="refresh" className="h-8 w-8 text-primary" />
                </span>
                Estamos validando tu cuenta, por favor espera un momento.
              </div>
            ) : data?.status ? (
              <div className="flex flex-col items-center gap-4 text-sm">
                <MainLogo color="#F97316" classname="w-12" />
                Tú Cuenta ha sido confirmada con éxito, ¡Bienvenido a Carriapp!
              </div>
            ) : (
              <Alert variant={"destructive"} className="w-full">
                <OctagonAlert className="h-4 w-4" />
                <AlertTitle>Error en validar tú cuenta</AlertTitle>
                <AlertDescription>
                  El Token no es valido o ha expirado, por favor vuelve a crear
                  tu cuenta.
                </AlertDescription>
              </Alert>
            )}
          </div>

          {/* Solo mostrar el botón si hay error o si aún está cargando */}
          {(!data?.status || loading) && (
            <div className="flex justify-end gap-3">
              <Button type="button" onClick={() => navigate("/")}>
                Volver al Inicio
              </Button>
            </div>
          )}
        </div>
      </CardContent>

      {/* Solo mostrar el footer con countdown si la validación fue exitosa y checkAuth se completó */}
      {data?.status && !loading && authCompleted && (
        <CardFooter className="text-center text-sm text-muted-foreground">
          Te redireccionaremos a la página de inicio en {countdown} segundos.
        </CardFooter>
      )}
    </Card>
  );
};

const ResetPasswordDialog: React.FC<{ token: string }> = ({ token }) => {
  const [confirmOpen, setCofirmOpen] = useState<boolean>(false);
  const [triggerFetch, setTriggerFetch] = useState(false);
  const [requestOptions, setRequestOptions] = useState<FetchDataOptions | null>(
    null,
  );
  const reCaptchaIsReady = useRecaptchaReady();
  const navigate = useNavigate();

  const { data, loading, error } = useFetch<statusResponse>(
    triggerFetch ? endPointValidateToken : null,
    requestOptions ?? undefined,
  );

  useEffect(() => {
    const getRecaptchaToken = async () => {
      if (reCaptchaIsReady) {
        try {
          const recaptchaToken = await window.grecaptcha.execute(
            RECAPTCHA_SITE_KEY,
            { action: "login" },
          );
          const opts: FetchDataOptions = {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: {
              token: token,
              recaptchaToken: recaptchaToken,
            },
          };
          setRequestOptions(opts);
          setTriggerFetch(true);
        } catch (error) {
          console.error("Error ejecutando reCAPTCHA:", error);
        }
      }
    };
    getRecaptchaToken();
  }, [reCaptchaIsReady, token]);

  const profileFormShema = z
    .object({
      token: z.string().min(1, { message: "El token es requerido" }),
      new_password: z
        .string()
        .min(8, { message: "La contraseña debe tener más de 8 carácteres" })
        .regex(/[a-zA-Z]/, { message: "Debe contener al menos una letra" })
        .regex(/[0-9]/, { message: "Debe contener al menos un número" }),
      passwordConfirmation: z
        .string()
        .min(8, { message: "La contraseña debe tener más de 8 carácteres" }),
    })
    .refine(
      (data) => {
        return data.new_password === data.passwordConfirmation;
      },
      {
        message: "Las contraseñas no coinciden",
        path: ["passwordConfirmation"],
      },
    );
  type profileFormvalues = z.infer<typeof profileFormShema>;

  const defaultValues: Partial<profileFormvalues> = {
    token: token,
    new_password: "",
    passwordConfirmation: "",
  };

  const form = useForm<profileFormvalues>({
    resolver: zodResolver(profileFormShema),
    defaultValues,
    mode: "onChange",
  });

  const onSubmit = async (data: profileFormvalues) => {
    try {
      const token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, {
        action: "PasswordReset",
      });

      const formData = {
        ...data,
        recaptchaToken: token,
      };

      const response = await fetch(endPointPasswordReset, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
        credentials: "include",
      });
      if (!response.ok) {
        const message = await response.json();

        throw new Error(message.detail);
      }

      const responseData = (await response.json()) as {
        status: boolean;
        message?: string;
      };
      if (responseData.status) {
        setCofirmOpen(true);
      } else {
        toast.error(
          responseData.message ||
            "Ha ocurrido un error al actualizar tu contraseña",
        );
      }
    } catch (e) {
      const errMsg = e instanceof Error ? e.message : String(e);
      toast.error(
        "Ha ocurrido un error al actualizar tu contraseña - " + errMsg,
      );
    }
  };
  return (
    <>
      <Card className="w-fit">
        <CardHeader className="font-semibold">
          <CardTitle>Recupera tu contraseña</CardTitle>
          <CardDescription>
            Por favor ingresa tu nueva contraseña
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading || !triggerFetch ? (
            <div className="flex flex-col items-center justify-center gap-4  text-muted-foreground text-sm">
              <Spinner className="w-8 h-8" />
              Estamos validando el token, por favor espera un momento.
            </div>
          ) : (
            <div className="mx-auto grid w-full lg:w-[350px] gap-6">
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="space-y-4"
                >
                  <FormField
                    control={form.control}
                    name="new_password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Contraseña</FormLabel>
                        <FormControl>
                          <Input
                            type="password"
                            {...field}
                            autoComplete="current-password"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="passwordConfirmation"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Confirmar Contraseña</FormLabel>
                        <FormControl>
                          <Input
                            type="password"
                            {...field}
                            autoComplete="current-password"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="flex justify-end gap-3">
                    <Button type="submit" disabled={loading}>
                      {loading ? "Procesando..." : "Restablecer"}
                    </Button>
                  </div>
                </form>
              </Form>
            </div>
          )}
        </CardContent>
      </Card>
      <AlertDialog onOpenChange={setCofirmOpen} open={confirmOpen}>
        {/* <AlertDialogTrigger>Open</AlertDialogTrigger> */}
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Tú contraseña se ha actualizado correctamente
            </AlertDialogTitle>
            <AlertDialogDescription>
              Te llevaremos de regreso a la paǵina inicial para que inicies
              sesión.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => navigate("/")}>
              Continuar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

// interface NoLoginHeaderProps {
//     loginAuth: (values: loginFormType, from:string) => Promise<void>;
// }

// const NoLoginHeader: React.FC<NoLoginHeaderProps> = ({loginAuth}) => {
//     const isDesktop = useMediaQuery(isDesktopSize);
//     return (
//         <header className="flex flex-col border-b bg-background z-50">

//             <div className="flex items-center mx-4 mb-1 md:my-2 lg:px-6 py-2 justify-between">
//                 <div className="md:w-[30%]">
//                     <Link to="/"><div className="flex flex-row items-center lg:ml-8 text-white lg:text-foreground gap-2">
//                         {/* <Bot size={48}/> */}
//                         <MainLogo color={(isDesktop) ? "#F97316" : "#F97316"} classname="w-12" />
//                         <span className={`font-bold mt-3 russo-one-regular ${(isDesktop) ? "logo-text" : "logo-text"}`} >Carri</span></div>
//                     </Link>
//                 </div>
//                 <div className="flex justify-end gap-2 w-[30%]">
//                     <div className="flex flex-col items-center gap-1">
//                         {/* <LoginDialog loginAuth={loginAuth}/> */}
//                     </div>
//                 </div>
//             </div>
//         </header>
//     );
// }

// const HowWorksDialog: React.FC<{ embedId: string }> = (embedId) => {
//     return (
//         <Dialog >
//             <DialogTrigger asChild>
//                 <Button variant="ghost" className="w-32 h-20 cursor-pointer hover:bg-transparent" asChild >
//                     <img src="/assets/play_button.webp" alt="play" />
//                 </Button>
//             </DialogTrigger>
//             <DialogContent className="px-0 pb-0 pt-0">
//                 <DialogHeader className="hidden">
//                     <DialogTitle className=""></DialogTitle>
//                     <DialogDescription></DialogDescription>
//                 </DialogHeader>
//                 <YoutubePlayer embedId="xIniY60SbkI" />
//             </DialogContent>
//         </Dialog>
//     );
// }
